import React, { useEffect, useState, createContext, useContext } from 'react';
type Country = 'eg' | 'ae' | 'sa';
interface CountryContextType {
  country: Country;
  setCountry: (country: Country) => void;
  currency: string;
  currencySymbol: string;
  phoneNumber: string;
  whatsappNumber: string;
  pricing: {
    starter: {
      monthly: number;
      yearly: number;
    };
    business: {
      monthly: number;
      yearly: number;
    };
    pro: {
      monthly: number;
      yearly: number;
    };
    custom: {
      monthly: string;
      yearly: string;
    };
  };
}
const CountryContext = createContext<CountryContextType | undefined>(undefined);
const COUNTRY_CONFIG = {
  eg: {
    currency: 'EGP',
    currencySymbol: 'ج.م',
    phoneNumber: '+20 150 808 7501',
    whatsappNumber: '201508087501',
    pricing: {
      starter: {
        monthly: 1500,
        yearly: 14400
      },
      business: {
        monthly: 4000,
        yearly: 36000
      },
      pro: {
        monthly: 7500,
        yearly: 63000
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  },
  ae: {
    currency: 'AED',
    currencySymbol: 'درهم',
    phoneNumber: '+971 50 854 6970',
    whatsappNumber: '971508546970',
    pricing: {
      starter: {
        monthly: 185,
        yearly: 1760
      },
      business: {
        monthly: 440,
        yearly: 3960
      },
      pro: {
        monthly: 920,
        yearly: 7700
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  },
  sa: {
    currency: 'SAR',
    currencySymbol: 'ر.س',
    phoneNumber: '+971 50 854 6970',
    whatsappNumber: '971508546970',
    pricing: {
      starter: {
        monthly: 190,
        yearly: 1800
      },
      business: {
        monthly: 450,
        yearly: 4050
      },
      pro: {
        monthly: 940,
        yearly: 7875
      },
      custom: {
        monthly: 'تواصل معنا',
        yearly: 'Contact Us'
      }
    }
  }
};
// Detect country from phone number
export function detectCountryFromPhone(phone: string): Country {
  const cleanPhone = phone.replace(/[\s\-\+]/g, '');
  if (cleanPhone.startsWith('20') || cleanPhone.startsWith('01')) {
    return 'eg';
  } else if (cleanPhone.startsWith('971') || cleanPhone.startsWith('05')) {
    return 'ae';
  } else if (cleanPhone.startsWith('966') || cleanPhone.startsWith('05')) {
    return 'sa';
  }
  return 'eg'; // Default to Egypt
}
// Map IP country code to our Country type
function mapCountryCode(code: string): Country | null {
  const map: Record<string, Country> = {
    EG: 'eg',
    AE: 'ae',
    SA: 'sa',
    // Gulf countries default to UAE
    BH: 'ae',
    KW: 'ae',
    OM: 'ae',
    QA: 'ae'
  };
  return map[code?.toUpperCase()] || null;
}
export function CountryProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [country, setCountry] = useState<Country>('eg');
  const [isDetected, setIsDetected] = useState(false);
  useEffect(() => {
    // 1. First check if user is logged in and has a phone number
    const userStr = localStorage.getItem('operiens_user');
    if (userStr) {
      try {
        const user = JSON.parse(userStr);
        if (user.phone) {
          const detectedCountry = detectCountryFromPhone(user.phone);
          setCountry(detectedCountry);
          localStorage.setItem('operines_country', detectedCountry);
          setIsDetected(true);
          return;
        }
        if (user.country) {
          setCountry(user.country);
          localStorage.setItem('operines_country', user.country);
          setIsDetected(true);
          return;
        }
      } catch (e) {
        console.error('Error parsing user data:', e);
      }
    }
    // 2. Check saved country preference
    const savedCountry = localStorage.getItem('operines_country') as Country;
    if (savedCountry && (savedCountry === 'eg' || savedCountry === 'ae' || savedCountry === 'sa')) {
      setCountry(savedCountry);
      setIsDetected(true);
      return;
    }
    // 3. Auto-detect country from IP (only if no saved preference)
    const detectFromIP = async () => {
      try {
        // Try ipapi.co first (free, no key needed, 1000 req/day)
        const response = await fetch('https://ipapi.co/json/', {
          signal: AbortSignal.timeout(5000)
        });
        if (response.ok) {
          const data = await response.json();
          const detected = mapCountryCode(data.country_code || data.country);
          if (detected) {
            setCountry(detected);
            localStorage.setItem('operines_country', detected);
            setIsDetected(true);
            return;
          }
        }
      } catch {
        // Silently fail - try fallback
      }
      try {
        // Fallback: ip-api.com (free, no key, 45 req/min)
        const response = await fetch('http://ip-api.com/json/?fields=countryCode', {
          signal: AbortSignal.timeout(5000)
        });
        if (response.ok) {
          const data = await response.json();
          const detected = mapCountryCode(data.countryCode);
          if (detected) {
            setCountry(detected);
            localStorage.setItem('operines_country', detected);
            setIsDetected(true);
            return;
          }
        }
      } catch {
        // Silently fail - use default (eg)
      }
      // If all detection fails, try browser timezone as last resort
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz.includes('Dubai') || tz.includes('Muscat')) {
          setCountry('ae');
          localStorage.setItem('operines_country', 'ae');
        } else if (tz.includes('Riyadh') || tz.includes('Kuwait') || tz.includes('Bahrain')) {
          setCountry('sa');
          localStorage.setItem('operines_country', 'sa');
        } else if (tz.includes('Cairo')) {
          setCountry('eg');
          localStorage.setItem('operines_country', 'eg');
        }
      } catch {
        // Use default
      }
      setIsDetected(true);
    };
    detectFromIP();
  }, []);
  const handleSetCountry = (newCountry: Country) => {
    setCountry(newCountry);
    localStorage.setItem('operines_country', newCountry);
  };
  const config = COUNTRY_CONFIG[country];
  return <CountryContext.Provider value={{
    country,
    setCountry: handleSetCountry,
    currency: config.currency,
    currencySymbol: config.currencySymbol,
    phoneNumber: config.phoneNumber,
    whatsappNumber: config.whatsappNumber,
    pricing: config.pricing
  }} data-id="element-736">
      {children}
    </CountryContext.Provider>;
}
export function useCountry() {
  const context = useContext(CountryContext);
  if (context === undefined) {
    throw new Error('useCountry must be used within a CountryProvider');
  }
  return context;
}